import React, {useMemo} from 'react';
import {Box, HStack, Image, Text, VStack, Icon} from '@chakra-ui/react';
import {Newspaper} from 'lucide-react';
import DOMPurify from 'dompurify';
import logo from '@/assets/images/logo-cartouche-secondary.png';
import dateUtil from '@/utils/dateUtil';
import {useLanguage} from '@/contexts/languageContext';

const detectVideoPlatform = (url) => {
    if (!url) return null;
    for (const platform of [
        {name: 'YouTube', color: '#FF0000', patterns: [/youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]+)/, /youtu\.be\/([a-zA-Z0-9_-]+)/], embed: (id) => `https://www.youtube.com/embed/${id}`},
        {name: 'Dailymotion', color: '#0066DC', patterns: [/dailymotion\.com\/video\/([a-zA-Z0-9]+)/, /dai\.ly\/([a-zA-Z0-9]+)/], embed: (id) => `https://www.dailymotion.com/embed/video/${id}`},
        {name: 'Vimeo', color: '#1AB7EA', patterns: [/vimeo\.com\/(\d+)/], embed: (id) => `https://player.vimeo.com/video/${id}`},
    ]) {
        for (const pattern of platform.patterns) {
            const match = url.match(pattern);
            if (match) return {name: platform.name, color: platform.color, videoId: match[1], embedUrl: platform.embed(match[1])};
        }
    }
    return null;
};

export const ArticlePreview = ({title, content, files, publishedAt}) => {
    const {language, t} = useLanguage();

    const rawFiles = files;
    const filesArray = typeof rawFiles === 'string' ? (() => { try { return JSON.parse(rawFiles); } catch { return []; } })() : (Array.isArray(rawFiles) ? rawFiles : []);
    const imageFiles = filesArray.filter(f => f?.type === 'image');
    const videoFiles = filesArray.filter(f => f?.type === 'video');
    const firstImageUrl = imageFiles[0]?.content ?? null;

    const mediaItems = useMemo(() => {
        return [
            ...imageFiles.map(f => ({...f, fileType: 'image'})),
            ...videoFiles.map(f => ({...f, fileType: 'video'}))
        ];
    }, [imageFiles, videoFiles]);

    const displayTitle = title?.[language] || title?.fr || title?.en || '';
    const displayContent = content?.[language] || content?.fr || content?.en || '';
    const displayDate = publishedAt ? dateUtil.format(new Date(publishedAt), language === 'fr' ? 'fr-FR' : 'en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }) : '';

    const htmlStyle = {
        lineHeight: '24px',
        fontSize: '16px',
        color: '#333',
    };

    return (
        <Box
            w="100%"
            h="100%"
            bg="white"
            overflow="hidden"
            display="flex"
            flexDirection="column"
        >
            {/* Image principale */}
            <Box h="224px" w="100%" bg="gray.100">
                {firstImageUrl ? (
                    <Image
                        src={firstImageUrl}
                        alt="Article thumbnail"
                        w="100%"
                        h="100%"
                        objectFit="cover"
                    />
                ) : (
                    <HStack w="100%" h="100%" justify="center" align="center">
                        <Icon as={Newspaper} boxSize={16} color="secondary.500"/>
                    </HStack>
                )}
            </Box>

            {/* Zone de contenu */}
            <VStack
                bg="white"
                borderRadius="2xl 2xl 0 0"
                pt="24px"
                px="32px"
                pb="16px"
                gap="16px"
                mt="-12px"
                align="stretch"
                flex={1}
                overflow="hidden"
            >
                {/* Thumbnails média (si plusieurs) */}
                {mediaItems.length > 1 && (
                    <HStack gap="20px" overflowX="auto" pb="8px">
                        {mediaItems.map((item, index) => (
                            <Box key={item.content + item.fileType} h="60px" w="60px" flexShrink={0}>
                                {item.fileType === 'image' ? (
                                    <Image
                                        src={item.content}
                                        alt={`Media ${index}`}
                                        w="60px"
                                        h="60px"
                                        objectFit="cover"
                                        borderRadius="md"
                                    />
                                ) : (
                                    <Box
                                        w="60px"
                                        h="60px"
                                        bg="gray.200"
                                        borderRadius="md"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Text fontSize="xs" textAlign="center">
                                            {detectVideoPlatform(item.content)?.name || 'Video'}
                                        </Text>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </HStack>
                )}

                {/* Titre */}
                <Text fontSize="32px" fontWeight="bold" lineHeight="40px">
                    {displayTitle}
                </Text>

                {/* Contenu HTML */}
                <Box flex={1} overflowY="auto" minH="0">
                    {displayContent ? (
                        <Box
                            dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(displayContent)}}
                            sx={{
                                '& p': {marginBottom: '16px'},
                                '& img': {maxW: '100%', height: 'auto'},
                                '& iframe': {width: '100%', height: 'auto', aspectRatio: '16/9'},
                                ...htmlStyle,
                            }}
                        />
                    ) : (
                        <Text color="gray.400" fontSize="14px">
                            {t('articles.noContent')}
                        </Text>
                    )}
                </Box>

                {/* Footer */}
                <HStack justify="space-between" align="center" pt="8px">
                    <HStack gap="8px" align="center">
                        <Box
                            w="40px"
                            h="40px"
                            borderRadius="full"
                            border="2px solid"
                            borderColor="primary.500"
                            overflow="hidden"
                        >
                            <Image
                                src={logo}
                                alt="Logo SNB"
                                w="100%"
                                h="100%"
                                objectFit="contain"
                            />
                        </Box>
                        <Text fontSize="14px">
                            {displayDate}
                        </Text>
                    </HStack>
                </HStack>
            </VStack>
        </Box>
    );
};

export default ArticlePreview;